/**
 * Safe date formatting utilities.
 * Prevents "Invalid Date" when the value is undefined, null, or not a valid date string.
 */

/**
 * Formats a date value to a locale date+time string.
 * Returns '-' for falsy or invalid values.
 */
export function formatDate(value: string | Date | undefined | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

/**
 * Formats a date value to a locale date-only string.
 * Returns '-' for falsy or invalid values.
 */
export function formatDateShort(value: string | Date | undefined | null): string {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
}

/**
 * Formats a date value to a compact date+time string (e.g. "Feb 7, 2026 3:45 PM").
 * Returns '-' for falsy or invalid values.
 */
export function formatDateTime(value: string | Date | undefined | null): string {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Computes a human-readable duration between two dates.
 * Returns '-' if either date is invalid.
 */
export function formatDuration(startDate: string | Date | undefined | null, endDate: string | Date | undefined | null): string {
  if (!startDate || !endDate) return '-';
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (isNaN(start) || isNaN(end)) return '-';
  const diffMs = end - start;
  if (diffMs < 0) return '-';
  const seconds = Math.floor(diffMs / 1000) % 60;
  const minutes = Math.floor(diffMs / 60000) % 60;
  const hours = Math.floor(diffMs / 3600000) % 24;
  const days = Math.floor(diffMs / 86400000);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
